export type Fund = 'BGA' | 'JH' | 'GOLD' | 'GGE' | 'OTP' | 'JAPAN' | 'CHARTERIS' | 'AMATI' | 'MINERS' | 'JAPANESG' | 'TECH' | 'ELECTRUM' | 'JHTECH' | 'ASIATECH' | 'BARINGSOIL' | 'SPWORLD' | 'SMALLCAP'
/** Every fund except the OTP overlay (the funds that hold a NAV-priced share class). */
export type PotFund = Exclude<Fund, 'OTP'>
export type NavUnits = 'pounds' | 'pence' | 'gbp'

export type StrikeColumn = 'inferredBgNav10' | 'inferredNav12'

export interface FundConfig {
  primaryAssetType: string
  strikeTime: string
  strikeShort: string
  navUnits: NavUnits
  /** nav_deltas column holding the frozen strike synth — explicit per fund, never inferred from labels. */
  strikeColumn: StrikeColumn
  /** Decimal places for pence display (sub-100p funds need 2 so a ~30 bp synth gap is visible). */
  navDecimals: 0 | 2
}

// Per-fund display config — single source of truth for strike time,
// unit convention, and primary asset type. GOLD strikes at 12:00 UK
// (like JH) and is stored in pence per [[gold-nav-pence]]. GGE (WS
// Guinness Global Energy, promotion Phase 4 2026-08-18) also strikes
// at 12:00 UK and quotes in pence on the HL factsheet. OTP (standalone
// Plan C launch) has no published NAV strike at all — it is a synthetic
// option-tracking overlay, not a fund share class — so strikeTime/
// strikeShort instead describe the 08:20 UK dealing-plan gate close,
// and navUnits is 'gbp' (not 'pounds'/'pence') to signal it is not a
// NAV-per-share quote.
// JAPAN (L&G Japan Index C Inc) VALUES at 12:00 UK (LgJapanIndexAssetProfile
// NavStrikeUk, dealing calendar lg_japan_index.json); 08:40 UK is its
// dealing-DECISION cutoff and is shown on the Japan Signal tab, never here
// (spec 2026-09-30 §4.1). ~90p NAV → 2 dp. Dark/gated fund — see
// FUND_REGISTRY.japan.requiresEntitlement.
// CHARTERIS (YFS Charteris Gold & Precious Metals I Inc, Charteris Phase 2.5
// Task 5) is a UK OEIC that VALUES at 12:00 UK — prospectus "The valuation
// point for the Funds is 12 noon on each Business Day", confirmed in
// Config/dealing_calendars/charteris_gold_pm.json and
// CharterisGoldPmAssetProfile.NavStrikeUk. Its 08:40 London SI decision is on
// its own Signal tab, never here. Pence, 2 dp, same shape as JAPAN. Gated.
// AMATI (WS Amati Strategic Metals, portfolio slug 'copper', Phase 2.5 lean
// page 2026-10-11) values at 12:00 UK like CHARTERIS; its 08:40 London COMEX
// copper (HG) decision is on its own Signal tab. Pence, 2 dp. Gated.
export const FUND_CONFIG: Record<Fund, FundConfig> = {
  BGA:   { primaryAssetType: 'BGA',   strikeTime: '10:00',    strikeShort: '10AM',  navUnits: 'pounds', strikeColumn: 'inferredBgNav10', navDecimals: 0 },
  JH:    { primaryAssetType: 'JHGTL', strikeTime: '12:00',    strikeShort: '12PM',  navUnits: 'pence',  strikeColumn: 'inferredNav12',   navDecimals: 0 },
  // GOLD ~100p → 2 dp, matching GGE/JAPAN (Paolo 2026-10-02).
  GOLD:  { primaryAssetType: 'GOLD',  strikeTime: '12:00',    strikeShort: '12PM',  navUnits: 'pence',  strikeColumn: 'inferredNav12',   navDecimals: 2 },
  // GGE ~70p → 2 dp as well (Paolo Q5, spec 2026-09-30 §7).
  GGE:   { primaryAssetType: 'GGE',   strikeTime: '12:00',    strikeShort: '12PM',  navUnits: 'pence',  strikeColumn: 'inferredNav12',   navDecimals: 2 },
  // OTP keeps today's behaviour (it never had a 12:00 column).
  OTP:   { primaryAssetType: 'OTP',   strikeTime: '08:20 UK', strikeShort: '08:20', navUnits: 'gbp',    strikeColumn: 'inferredBgNav10', navDecimals: 0 },
  JAPAN: { primaryAssetType: 'JAPAN', strikeTime: '12:00',    strikeShort: '12PM',  navUnits: 'pence',  strikeColumn: 'inferredNav12',   navDecimals: 2 },
  CHARTERIS: { primaryAssetType: 'CHARTERIS', strikeTime: '12:00', strikeShort: '12PM', navUnits: 'pence', strikeColumn: 'inferredNav12', navDecimals: 2 },
  AMATI: { primaryAssetType: 'AMATI', strikeTime: '12:00', strikeShort: '12PM', navUnits: 'pence', strikeColumn: 'inferredNav12', navDecimals: 2 },
  // MINERS (ES Baker Steel Gold & Precious Metals B Acc, Phase 1C 2026-10-25): 12:00 UK valuation, pence, 2 dp. Gated.
  MINERS: { primaryAssetType: 'MINERS', strikeTime: '12:00', strikeShort: '12PM', navUnits: 'pence', strikeColumn: 'inferredNav12', navDecimals: 2 },
  // JAPANESG (L&G Future World ESG Tilted and Optimised Japan Index I Acc, 2026-10-28): 12:00 UK valuation, pence (~81p), 2 dp. Gated.
  JAPANESG: { primaryAssetType: 'JAPANESG', strikeTime: '12:00', strikeShort: '12PM', navUnits: 'pence', strikeColumn: 'inferredNav12', navDecimals: 2 },
  // TECH (Liontrust Global Technology C Acc, 2026-10-28): 12:00 UK valuation (docs/research/2026-10-28-liontrust-valuation-point-bst.md), pence (~918p), 2 dp. Gated.
  TECH: { primaryAssetType: 'TECH', strikeTime: '12:00', strikeShort: '12PM', navUnits: 'pence', strikeColumn: 'inferredNav12', navDecimals: 2 },
  // ELECTRUM (SVS Baker Steel Electrum B Acc, 2026-10-31): 12:00 UK valuation, pence, 2 dp. Gated.
  ELECTRUM: { primaryAssetType: 'ELECTRUM', strikeTime: '12:00', strikeShort: '12PM', navUnits: 'pence', strikeColumn: 'inferredNav12', navDecimals: 2 },
  // JHTECH, ASIATECH, BARINGSOIL, SPWORLD (2026-10-31, Config/fund_packs/<key>.json): 12:00 UK valuation, pence
  // feeds (BARINGSOIL's HL page reads 3,185p; FT publishes pounds), 2 dp. Gated, dark.
  JHTECH: { primaryAssetType: 'JHTECH', strikeTime: '12:00', strikeShort: '12PM', navUnits: 'pence', strikeColumn: 'inferredNav12', navDecimals: 2 },
  ASIATECH: { primaryAssetType: 'ASIATECH', strikeTime: '12:00', strikeShort: '12PM', navUnits: 'pence', strikeColumn: 'inferredNav12', navDecimals: 2 },
  BARINGSOIL: { primaryAssetType: 'BARINGSOIL', strikeTime: '12:00', strikeShort: '12PM', navUnits: 'pence', strikeColumn: 'inferredNav12', navDecimals: 2 },
  SPWORLD: { primaryAssetType: 'SPWORLD', strikeTime: '12:00', strikeShort: '12PM', navUnits: 'pence', strikeColumn: 'inferredNav12', navDecimals: 2 },
  // SMALLCAP (CT US Smaller Companies Integrated C Inc, 2026-10-31): 12:00 UK valuation, pence (~308p), 2 dp. Gated, dark.
  SMALLCAP: { primaryAssetType: 'SMALLCAP', strikeTime: '12:00', strikeShort: '12PM', navUnits: 'pence', strikeColumn: 'inferredNav12', navDecimals: 2 },
}

// ── Fund registry (GGE promotion Phase 4, 2026-08-18) ─────────────────────
// One place tying together everything a fund needs to exist in the SPA:
// the portfolio slug (subdomain identity), the Fund discriminator used in
// API calls, the display label, and the hostnames that resolve to it.
// Previous fund launches missed UI surfaces because these facts were
// scattered as ~25 literals — new code should consume this registry (or
// FUND_CONFIG above) instead of hardcoding fund lists.
export type PortfolioSlug = 'bga' | 'jh' | 'gold' | 'gge' | 'otp' | 'japan' | 'silver' | 'copper' | 'miners' | 'japanesg' | 'tech' | 'electrum'
  | 'jhtech' | 'asiatech' | 'baringsoil' | 'spworld' | 'smallcap'

export interface FundRegistryEntry {
  /** Subdomain identity + ?portfolio= override value. */
  slug: PortfolioSlug
  /** The Fund discriminator threaded through fund-aware API calls. */
  fund: Fund
  /** Human label shown in pills/headers. */
  label: string
  /**
   * True when a user must be individually entitled (via /api/auth/me's
   * entitledFunds) to see this fund. Registered but gated funds are
   * excluded from ALL_PORTFOLIOS/ALL_FUNDS (see REGISTERED_PORTFOLIOS
   * below) so existing consumers that iterate ALL_* never widen to include
   * them; visibility-aware consumers use useVisibleFunds instead.
   */
  requiresEntitlement: boolean
  /** Hostnames that resolve to this portfolio (see usePortfolio). */
  hostnames: string[]
  /**
   * True when this fund's standalone backtest is the pure 08:40 sign rule —
   * no auto-optimise, pure step-aside, no schedule (japan today).
   */
  backtestSignRuleOnly?: boolean
  /** Futures root the generic sign-rule Signal tab reads the 08:40 move off (e.g. 'GC'); unset = bespoke tab. */
  signalRoot?: string
  /** Label of the exchange-close anchor the move is measured from (e.g. 'NY close'); unset = bespoke tab. */
  signalAnchorLabel?: string
}

export const FUND_REGISTRY: Record<PortfolioSlug, FundRegistryEntry> = {
  bga: {
    slug: 'bga', fund: 'BGA', label: 'BGA', requiresEntitlement: false,
    // 2026-10-12 (FOF spec §3.3): sidewinder. / debug.sidewinder. moved to HOME_PAGE (FOF is the
    // site's default address); bga.buxushedgefund.com stays BGA.
    hostnames: ['bga.buxushedgefund.com'],
  },
  jh: {
    slug: 'jh', fund: 'JH', label: 'JH', requiresEntitlement: false,
    hostnames: ['jh.buxushedgefund.com', 'debug.jh.buxushedgefund.com'],
  },
  gold: {
    slug: 'gold', fund: 'GOLD', label: 'GOLD', requiresEntitlement: false,
    // 91 = Ninety One (the gold fund's manager) — alias added 2026-07-26
    // when the gold Caddy vhost was re-instated.
    hostnames: ['gold.buxushedgefund.com', '91.buxushedgefund.com'],
  },
  gge: {
    slug: 'gge', fund: 'GGE', label: 'GGE', requiresEntitlement: false,
    // Both DNS entries added by paolo 2026-08-18 (gold's two-alias
    // precedent): the slug name and the fund's plain-English identity.
    hostnames: ['gge.buxushedgefund.com', 'energy.buxushedgefund.com'],
  },
  otp: {
    slug: 'otp', fund: 'OTP', label: 'OTP', requiresEntitlement: false,
    hostnames: ['otp.buxushedgefund.com'],
  },
  japan: {
    slug: 'japan', fund: 'JAPAN', label: 'Japan', requiresEntitlement: true,
    hostnames: ['japan.buxushedgefund.com'],
    backtestSignRuleOnly: true,
  },
  // Charteris Phase 2.5 Task 5: the second gated fund. Portfolio slug renamed
  // 'charteris' → 'silver' on 2026-10-10 (Paolo): ?portfolio=silver and
  // silver.buxushedgefund.com, gold's commodity-named precedent. Only the SPA
  // slug/label changed — the Fund discriminator stays 'CHARTERIS' because it
  // is the API/DB fund_id (FundId.CHARTERIS, V129 CHECKs, Redis keys).
  silver: {
    slug: 'silver', fund: 'CHARTERIS', label: 'Silver', requiresEntitlement: true,
    hostnames: ['silver.buxushedgefund.com'],
    backtestSignRuleOnly: true,
  },
  // AMATI Phase 2.5 lean fund page (2026-10-11, Paolo): the third gated fund,
  // named for its commodity like silver — ?portfolio=copper and
  // copper.buxushedgefund.com. SPA slug/label only: the Fund discriminator
  // stays 'AMATI' because it is the API/DB fund_id (FundId.AMATI, Redis keys
  // latest:signal:amati, ?fund=amati).
  copper: {
    slug: 'copper', fund: 'AMATI', label: 'Copper', requiresEntitlement: true,
    hostnames: ['copper.buxushedgefund.com'],
    backtestSignRuleOnly: true,
  },
  // MINERS (ES Baker Steel Gold & Precious Metals, Phase 1C 2026-10-25): the fourth gated fund, the first on the
  // generic config-driven fund pack (Config/fund_packs/miners.json). Slug = fund id lower-cased; sign rule off
  // COMEX gold (GC) since the previous NY close. Mirrored against the pack by fundConfig.pack.test.ts.
  miners: {
    slug: 'miners', fund: 'MINERS', label: 'Miners', requiresEntitlement: true,
    hostnames: ['miners.buxushedgefund.com'],
    backtestSignRuleOnly: true,
    signalRoot: 'GC',
    signalAnchorLabel: 'NY close',
  },
  // JAPANESG (L&G Future World ESG Japan, 2026-10-28): the ninth fund, the second on the generic fund pack
  // (Config/fund_packs/japanesg.json), added alongside JAPAN (which stays). Sign rule off Nikkei 225 Yen (NIY)
  // since the TSE 15:30 JST close. Mirrored against the pack by fundConfig.pack.test.ts.
  japanesg: {
    slug: 'japanesg', fund: 'JAPANESG', label: 'Japan ESG', requiresEntitlement: true,
    hostnames: ['japanesg.buxushedgefund.com'],
    backtestSignRuleOnly: true,
    signalRoot: 'NIY',
    signalAnchorLabel: 'TSE close',
  },
  // TECH (Liontrust Global Technology, 2026-10-28): the tenth fund, the third on the generic fund pack
  // (Config/fund_packs/tech.json), added alongside JH (which stays). Sign rule off Nasdaq-100 futures (NQ)
  // since the previous NY close, on BGA/JH's shared NQ series. Mirrored against the pack by fundConfig.pack.test.ts.
  tech: {
    slug: 'tech', fund: 'TECH', label: 'Tech', requiresEntitlement: true,
    hostnames: ['tech.buxushedgefund.com'],
    backtestSignRuleOnly: true,
    signalRoot: 'NQ',
    signalAnchorLabel: 'NY close',
  },
  // ELECTRUM (SVS Baker Steel Electrum B Acc, 2026-10-31): the eleventh fund, the fourth on the generic fund pack
  // (Config/fund_packs/electrum.json), added alongside Amati (which stays). Sign rule off COMEX copper futures (HG)
  // since the previous NY close, on the shared hg_0840 series. Mirrored against the pack by fundConfig.pack.test.ts.
  electrum: {
    slug: 'electrum', fund: 'ELECTRUM', label: 'Electrum', requiresEntitlement: true,
    hostnames: ['electrum.buxushedgefund.com'],
    backtestSignRuleOnly: true,
    signalRoot: 'HG',
    signalAnchorLabel: 'NY close',
  },
  // JHTECH, ASIATECH, BARINGSOIL, SPWORLD (2026-10-31): funds twelve to fifteen on the generic fund pack
  // (Config/fund_packs/<key>.json), dark additions: no grants, no pot rows. Sign rule since the previous NY close:
  // JHTECH and ASIATECH off NQ (BGA/JH's nq_0840 series), BARINGSOIL off CL (GGE's cl_0840 series), SPWORLD off ES
  // (its own es_0840 series). Mirrored against the packs by fundConfig.pack.test.ts.
  jhtech: {
    slug: 'jhtech', fund: 'JHTECH', label: 'JHTech', requiresEntitlement: true,
    hostnames: ['jhtech.buxushedgefund.com'],
    backtestSignRuleOnly: true,
    signalRoot: 'NQ',
    signalAnchorLabel: 'NY close',
  },
  asiatech: {
    slug: 'asiatech', fund: 'ASIATECH', label: 'AsiaTech', requiresEntitlement: true,
    hostnames: ['asiatech.buxushedgefund.com'],
    backtestSignRuleOnly: true,
    signalRoot: 'NQ',
    signalAnchorLabel: 'NY close',
  },
  baringsoil: {
    slug: 'baringsoil', fund: 'BARINGSOIL', label: 'BaringsOil', requiresEntitlement: true,
    hostnames: ['baringsoil.buxushedgefund.com'],
    backtestSignRuleOnly: true,
    signalRoot: 'CL',
    signalAnchorLabel: 'NY close',
  },
  spworld: {
    slug: 'spworld', fund: 'SPWORLD', label: 'SPWorld', requiresEntitlement: true,
    hostnames: ['spworld.buxushedgefund.com'],
    backtestSignRuleOnly: true,
    signalRoot: 'ES',
    signalAnchorLabel: 'NY close',
  },
  // SMALLCAP (2026-10-31): fund sixteen on the generic fund pack, a dark addition: no grants, no pot rows. Sign rule
  // since the previous NY close off RTY (its own rty_0840 series). Mirrored against the pack by fundConfig.pack.test.ts.
  smallcap: {
    slug: 'smallcap', fund: 'SMALLCAP', label: 'SmallCap', requiresEntitlement: true,
    hostnames: ['smallcap.buxushedgefund.com'],
    backtestSignRuleOnly: true,
    signalRoot: 'RTY',
    signalAnchorLabel: 'NY close',
  },
}

/** Every portfolio slug the SPA knows about, gated or not, gated last (japan, silver, copper, miners, japanesg, tech, electrum, jhtech, asiatech, baringsoil, spworld, smallcap). */
export const REGISTERED_PORTFOLIOS: ReadonlyArray<PortfolioSlug> = ['bga', 'jh', 'gold', 'gge', 'otp', 'japan', 'silver', 'copper', 'miners', 'japanesg', 'tech', 'electrum', 'jhtech', 'asiatech', 'baringsoil', 'spworld', 'smallcap']

/** All Fund discriminators registered, gated or not (mirrors REGISTERED_PORTFOLIOS). */
export const REGISTERED_FUNDS: ReadonlyArray<Fund> = REGISTERED_PORTFOLIOS.map(s => FUND_REGISTRY[s].fund)

/**
 * The non-gated portfolio slugs, in canonical fund order. Stays exactly the
 * five live funds — a gated registry entry (japan) is deliberately excluded
 * so existing consumers that iterate ALL_PORTFOLIOS/ALL_FUNDS never widen to
 * show it. Visibility-aware consumers use useVisibleFunds instead.
 */
export const ALL_PORTFOLIOS: ReadonlyArray<PortfolioSlug> =
  REGISTERED_PORTFOLIOS.filter(s => !FUND_REGISTRY[s].requiresEntitlement)

/** Registry slugs that require an entitlement, registered order (japan today). */
export const GATED_PORTFOLIOS: ReadonlyArray<PortfolioSlug> =
  REGISTERED_PORTFOLIOS.filter(s => FUND_REGISTRY[s].requiresEntitlement)

/** The admin fund-access grid's columns: every registered fund but OTP. */
export const ACCESS_GRID_FUNDS: ReadonlyArray<Fund> = REGISTERED_FUNDS.filter(f => f !== 'OTP')

export function registryEntryForFund(fund: Fund): FundRegistryEntry {
  const entry = Object.values(FUND_REGISTRY).find(e => e.fund === fund)
  if (!entry) throw new Error(`no registry entry for fund ${fund}`)
  return entry
}

// FOF home redesign 2026-11-02 (spec §6): the PortfolioPill cycle (PORTFOLIO_PILL_CYCLE / nextPortfolioInCycle)
// is gone — BlueBoxPill always goes home and navigation is driven from the FOF page.

/**
 * Canonical hostname for a fund — the target of PortfolioPill
 * click-throughs. Prefers the hostname that starts with the fund's slug
 * (e.g. `bga.buxushedgefund.com`, matching the pill's longstanding
 * convention), NOT `FUND_REGISTRY[slug].hostnames[0]` (BGA's first-listed
 * entry used to be `sidewinder.buxushedgefund.com`; since 2026-10-12 that
 * host belongs to the FOF home page). Falls back to hostnames[0] if no
 * slug-matching entry exists.
 */
export function primaryHostname(slug: PortfolioSlug): string {
  const hostnames = FUND_REGISTRY[slug].hostnames
  return hostnames.find(h => h.startsWith(`${slug}.`)) ?? hostnames[0]
}

/** All (non-gated) Fund discriminators in canonical order (allocation-row order). */
export const ALL_FUNDS: ReadonlyArray<Fund> = ALL_PORTFOLIOS.map(s => FUND_REGISTRY[s].fund)

// ── Entitlement gating (Japan Phase 2 final-review fix I3, spec 2026-09-27) ──
// Client-side mirror of FundProfile.RequiresEntitlement (C#
// SideWinder.Subledger/Domain/FundProfiles.cs): funds a user only sees when
// /api/auth/me's entitledFunds lists them.
//
// Fix round 1 (2026-09-28 review): GATED_FUNDS used to be a hand-written
// literal, a second source of truth alongside FUND_REGISTRY's
// requiresEntitlement — the two could silently drift (a fund marked
// requiresEntitlement in the registry but missing from this list would fail
// OPEN). Now derived from the registry so there is exactly one place that
// says which funds are gated.
export const GATED_FUNDS: ReadonlyArray<Fund> =
  REGISTERED_PORTFOLIOS.filter(s => FUND_REGISTRY[s].requiresEntitlement).map(s => FUND_REGISTRY[s].fund)

export function isGatedFund(fund: string): boolean {
  return (GATED_FUNDS as readonly string[]).includes(fund)
}

/**
 * The single shared fund-visibility predicate. `entitled === null` means the
 * entitlement list is not loaded (or the /api/auth/me fetch failed): gated
 * funds are hidden (fail closed), every other fund is shown (so the four
 * live funds never flicker away). Once loaded it is an exact filter.
 */
export function isFundVisibleFor(entitled: ReadonlyArray<string> | null, fund: string): boolean {
  if (entitled === null) return !(GATED_FUNDS as readonly string[]).includes(fund)
  return entitled.includes(fund)
}

/**
 * Per-user fund access (spec 2026-09-30): the four live funds an admin can switch off per user.
 * Mirrors C# FundAccessService.LiveAccessFunds. JAPAN, CHARTERIS, AMATI, MINERS, JAPANESG, TECH, ELECTRUM, JHTECH, ASIATECH, BARINGSOIL, SPWORLD and SMALLCAP are NOT here — they stay on the
 * entitlement rule (isFundVisibleFor); never derive this from requiresEntitlement.
 */
export const LIVE_ACCESS_FUNDS: ReadonlyArray<Fund> = ['BGA', 'JH', 'GOLD', 'GGE']

/**
 * Live-fund access predicate. `accessible === null` — /me not loaded, fetch failed, or an API that
 * predates accessibleFunds — FAILS OPEN (every live fund accessible), matching the server's
 * fail-open rule. Non-live funds (OTP, JAPAN) always pass here.
 */
export function isLiveFundAccessibleFor(accessible: ReadonlyArray<string> | null, fund: string): boolean {
  if (!(LIVE_ACCESS_FUNDS as readonly string[]).includes(fund)) return true
  return accessible === null || accessible.includes(fund)
}

/** Portfolio slug → Fund discriminator (e.g. 'gge' → 'GGE'). */
export function fundForPortfolio(slug: PortfolioSlug): Fund {
  return FUND_REGISTRY[slug].fund
}

/**
 * Flattened hostname → slug map consumed by usePortfolio's resolver. Built
 * from REGISTERED_PORTFOLIOS (not ALL_PORTFOLIOS) so a gated fund's own
 * hostname (japan.buxushedgefund.com) still resolves to its portfolio —
 * entitlement is enforced by what the SPA shows once resolved, not by
 * hostname routing.
 */
export const HOSTNAME_TO_PORTFOLIO: Record<string, PortfolioSlug> = Object.fromEntries(
  REGISTERED_PORTFOLIOS.flatMap(slug => FUND_REGISTRY[slug].hostnames.map(h => [h, slug])),
)

export function fmtNav(v: number | null | undefined, fund: Fund): string {
  if (v == null) return '--'
  const cfg = FUND_CONFIG[fund]
  const pence = cfg.navUnits === 'pence' ? v : v * 100
  if (cfg.navDecimals === 0) return Math.round(pence).toLocaleString('en-GB') + 'p'
  return pence.toLocaleString('en-GB', { minimumFractionDigits: cfg.navDecimals, maximumFractionDigits: cfg.navDecimals }) + 'p'
}

export function fmtShortDate(dateStr: string | null): string {
  if (!dateStr) return '--'
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
}

// ── Backtest ids (Task 10, spec 2026-09-30 §6) ────────────────────────────
// Jobs are keyed by the engine profile's asset id; JAPAN's is 'lg_japan_index'
// because 'japan' is the shared-pot job id server-side (IsSharedPotJob).
// Configs stay on the fund slug (?fund=japan → backtest_configs/japan.json).
// CHARTERIS (portfolio slug 'silver') runs as 'charteris_gold_pm' (C#
// BacktestJobService.CharterisAssetId) and saves configs under ?fund=charteris
// (backtest_configs/charteris.json, FundProfiles.Parse("charteris")) — the
// server never sees the 'silver' slug.
// AMATI (portfolio slug 'copper') likewise runs as 'amati_strategic_metals'
// and saves configs under ?fund=amati (backtest_configs/amati.json).
export type BacktestAssetId = 'bga' | 'jh' | 'gold' | 'gge' | 'otp' | 'lg_japan_index' | 'charteris_gold_pm' | 'amati_strategic_metals' | 'baker_steel_gold_pm' | 'lg_fw_esg_japan' | 'liontrust_global_tech' | 'baker_steel_electrum'
  | 'jh_sustainable_future_tech' | 'lg_pacific_index' | 'barings_global_resources' | 'fidelity_open_world' | 'ct_us_smaller_companies'
export type BacktestConfigFund = 'bga' | 'jh' | 'gold' | 'gge' | 'otp' | 'japan' | 'charteris' | 'amati' | 'miners' | 'japanesg' | 'tech' | 'electrum'
  | 'jhtech' | 'asiatech' | 'baringsoil' | 'spworld' | 'smallcap'

export function backtestAssetId(slug: PortfolioSlug): BacktestAssetId {
  if (slug === 'silver') return 'charteris_gold_pm'
  if (slug === 'copper') return 'amati_strategic_metals'
  if (slug === 'miners') return 'baker_steel_gold_pm'
  if (slug === 'japanesg') return 'lg_fw_esg_japan'
  if (slug === 'tech') return 'liontrust_global_tech'
  if (slug === 'electrum') return 'baker_steel_electrum'
  if (slug === 'jhtech') return 'jh_sustainable_future_tech'
  if (slug === 'asiatech') return 'lg_pacific_index'
  if (slug === 'baringsoil') return 'barings_global_resources'
  if (slug === 'spworld') return 'fidelity_open_world'
  if (slug === 'smallcap') return 'ct_us_smaller_companies'
  return slug === 'japan' ? 'lg_japan_index' : slug
}

export function backtestConfigFund(slug: PortfolioSlug): BacktestConfigFund {
  if (slug === 'silver') return 'charteris'
  if (slug === 'copper') return 'amati'
  return slug
}

// ── FOF home page (spec 2026-10-12 FOF home and navigation) ──
// FOF is a navigation PAGE, never a Fund: it is deliberately absent from FUND_REGISTRY,
// REGISTERED_PORTFOLIOS/FUNDS, ALL_*, GATED_* and ACCESS_GRID_FUNDS, so nothing that loops over
// funds (fund access, allocations, pot weights, feeds) ever sees it. Only the resolver
// (usePage/resolvePage), the pill and the Dashboard page choice use PageSlug.
export type PageSlug = PortfolioSlug | 'fof'

export const HOME_PAGE = {
  slug: 'fof' as const,
  label: 'FOF' as const,
  hostnames: ['fof.buxushedgefund.com', 'sidewinder.buxushedgefund.com', 'debug.sidewinder.buxushedgefund.com'],
}

/** Every page the SPA can show: FOF first, then every registered fund portfolio. */
export const ALL_PAGES: ReadonlyArray<PageSlug> = ['fof', ...REGISTERED_PORTFOLIOS]

/** Hostname → page: every fund hostname plus FOF's. */
export const PAGE_HOSTNAME_TO_SLUG: Record<string, PageSlug> = {
  ...HOSTNAME_TO_PORTFOLIO,
  ...Object.fromEntries(HOME_PAGE.hostnames.map(h => [h, 'fof' as const])),
}

/** Pill/header label for a page ("Gold" keeps its historical casing). */
export function pageLabel(slug: PageSlug): string {
  if (slug === 'fof') return HOME_PAGE.label
  if (slug === 'gold') return 'Gold'
  return FUND_REGISTRY[slug].label
}

/** Canonical hostname for a page — FOF's own subdomain, else the fund's primaryHostname. */
export function primaryPageHostname(slug: PageSlug): string {
  return slug === 'fof' ? HOME_PAGE.hostnames[0] : primaryHostname(slug)
}
